import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Option } from 'react-bootstrap-typeahead/types/types';

import { utilityLinkConfg } from '../../configs/UtilityLinkingConfig';
import { planConnectorLink } from '../../services/helpers/connectorLink';
import { ZeosApiContext } from '../../context/ZeosApiContext';
import { IPortfolioItem } from '../../mock-data/portfolioData';
import { IZeosUser } from '../../services/auth/IZeosUser';
import { Document } from '../../services/zeos-api/collections/Document';
import { DocumentUploadRequest } from '../../services/zeos-api/contracts/Document';
import { IUapiFormRequest } from '../../services/zeos-api/contracts/Uapi';
import { UapiStatus } from '../../services/zeos-api/enums/Uapi';
import {
  BILL_UPLOAD_ACCEPT,
  fileKey,
  getDocumentTypeId,
} from '../../services/zeos-api/helpers/DocumentUploadHelpers';
import { createLogger } from '../../services/Logger';
import { ZeosButton } from '../Shared/ZeosButton';
import { ZeosSpinner } from '../Shared/ZeosSpinner';
import { ZeosTypeAhead } from '../Shared/ZeosTypeAhead';
import { BayouEmbed } from './BayouEmbed';

// entityTypeId/documentCategoryId must match Reference__EntityType (Property=20)
// and Reference__DocumentCategory (UtilityBill=12) — same constants UapiManual.tsx uses.
const PROPERTY_ENTITY_TYPE_ID = 20;
const UTILITY_BILL_CATEGORY_ID = 12;

interface IUtilityOption {
  label: string;
  utilityCode: string;
  partner: "uapi" | "bayou";
}

interface IProps {
  property: IPortfolioItem | null;
  zeosUser: IZeosUser | undefined;
  // utilityLinkType?: "uapi" | "bayou";
  utilityCode?: string;
  onClose: () => void;
  onStatusChange?: (status: UapiStatus) => void;
  showBayouButtons?: boolean;
  /**
   * Add-a-meter (task-4061): offer UtilityAPI utilities only and no bill upload. A second UtilityAPI
   * authorization adds its meters to the property; a second Bayou link replaces the property's Bayou meters.
   */
  uapiOnly?: boolean;
}

const POLL_INTERVAL_MS = 3000;
// ponytail: fixed 2.5 min ceiling. Webhook usually lands in seconds; if it routinely
// doesn't, raise this rather than adding backoff.
const POLL_TIMEOUT_MS = 150_000;
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

type UapiPhase = "idle" | "awaiting" | "linked" | "timedOut" | "error";

const log = createLogger("UtilityLinkSelector");

export const UtilityLinkSelector = (props: IProps) => {
  const api = useContext(ZeosApiContext);
  const [isOpeningUapi, setIsOpeningUapi] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<
    "uapi" | "bayou" | undefined
  >();
  const [selectedUtilityCode, setSelectedUtilityCode] = useState<string>("");
  const [uapiError, setUapiError] = useState<string | null>(null);
  const uapiWindowRef = useRef<Window | null>(null);
  const [uapiPhase, setUapiPhase] = useState<UapiPhase>("idle");
  const [isPopupClosed, setIsPopupClosed] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCancelledRef = useRef(false);

  const [mode, setMode] = useState<"connect" | "upload">("connect");
  const [linkNote, setLinkNote] = useState<string | null>(null);
  const [manualUtilityName, setManualUtilityName] = useState<string | null>(null);
  const [partnerFilter, setPartnerFilter] = useState<Array<"uapi" | "bayou"> | null>(null);
  const [autoSelected, setAutoSelected] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // Transform utilities from config to typeahead options
  const utilityOptions = useMemo(() => {
    const options: Option[] = [];
    utilityLinkConfg.partners.forEach((partner) => {
      if (props.uapiOnly && partner.partner !== "uapi") {
        return;
      }
      partner.utilities.forEach((utility) => {
        options.push({
          label: `${utility.utilityName} (${partner.partner})`,
          utilityCode: utility.utilityCode,
          partner: partner.partner,
        } as IUtilityOption);
      });
    });
    // Sort options by label alphabetically
    options.sort((a, b) => {
      const aLabel = (a as IUtilityOption).label.toLowerCase();
      const bLabel = (b as IUtilityOption).label.toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
    return options;
  }, [props.uapiOnly]);

  const visibleOptions = useMemo(() => {
    if (!partnerFilter) return utilityOptions;
    return utilityOptions.filter((option) =>
      partnerFilter.includes((option as IUtilityOption).partner)
    );
  }, [utilityOptions, partnerFilter]);

  // Set once the person picks, uploads or changes connector, so a late profile response never
  // overwrites what they did.
  const userTouched = useRef(false);

  // P06: a resolved service with one active connector skips the picker. Several is a choice.
  // None opens bill upload (add-a-meter only explains). Anything that is not RESOLVED leaves the
  // picker as it was. A utility pre-filled from onboarding (ONB-005) wins over all of this.
  useEffect(() => {
    const propertyId = props.property?.propertyId;
    if (!propertyId) return;
    let cancelled = false;
    (async () => {
      const response = await api.Territory.getProfileAsync(propertyId);
      if (cancelled) return;
      if (userTouched.current || props.utilityCode) return;
      const plan = planConnectorLink(response.data?.services, !!props.uapiOnly);
      setLinkNote(plan.note ?? null);
      setPartnerFilter(plan.partnerFilter ?? null);
      setManualUtilityName(plan.manualUtilityName ?? null);
      if (plan.manualUtilityName && !props.uapiOnly) {
        setMode("upload");
        setAutoSelected(false);
        return;
      }
      if (plan.preselectPartner && plan.preselectUtilityName) {
        // The profile carries the ZEOS utility, not the partner's utility code, so the only join to
        // the picker is the display name. Normalized, but a name that differs from the partner
        // list's spelling still misses; the picker then stays open for the person.
        const normalize = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();
        const wanted = normalize(`${plan.preselectUtilityName} (${plan.preselectPartner})`);
        const match = utilityOptions.find(
          (option) => normalize((option as IUtilityOption).label) === wanted
        ) as IUtilityOption | undefined;
        if (match) {
          setSelectedPartner(match.partner);
          setSelectedUtilityCode(match.utilityCode);
          setPendingSelection(match);
          setAutoSelected(true);
          return;
        }
        // The note would claim a preselection that did not happen.
        setLinkNote(null);
      }
      setAutoSelected(false);
    })().catch((error) => {
      log.error("Utility profile lookup failed", error);
    });
    return () => {
      cancelled = true;
    };
  }, [api, props.property?.propertyId, props.uapiOnly, props.utilityCode, utilityOptions]);

  // Pre-fill from a utility confirmed earlier in onboarding (ONB-005), if it
  // maps to one of the options above. Uncontrolled (defaultSelected) so the
  // field stays fully editable -- the parent only mounts this component once
  // props.utilityCode is already resolved, so this is stable by first render.
  const defaultSelectedOption = useMemo(() => {
    if (!props.utilityCode) return undefined;
    const match = utilityOptions.find(
      (option) => (option as IUtilityOption).utilityCode === props.utilityCode
    );
    return match ? [match] : undefined;
  }, [props.utilityCode, utilityOptions]);

  // The typeahead only records what's picked (or pre-filled); it no longer
  // triggers the uapi/bayou flow by itself -- that's an explicit Confirm now.
  const [pendingSelection, setPendingSelection] = useState<
    IUtilityOption | undefined
  >(defaultSelectedOption?.[0] as IUtilityOption | undefined);

  // Bumped by Cancel to force ZeosTypeAhead to remount with no default --
  // it's uncontrolled, so that's the only way to clear its displayed value.
  const [typeaheadResetKey, setTypeaheadResetKey] = useState(0);

  // Handle utility selection from typeahead
  const handleUtilitySelection = (selected: Option[]) => {
    userTouched.current = true;
    setPendingSelection(
      selected.length > 0 ? (selected[0] as IUtilityOption) : undefined
    );
  };

  const handleCancelSelection = () => {
    setPendingSelection(undefined);
    setTypeaheadResetKey((key) => key + 1);
    props.onClose();
  };

  // Cancel from the uapi/bayou screen: back to the typeahead, keeping
  // whatever was picked/confirmed (ZeosTypeAhead remounts fresh here since it
  // was unmounted while selectedPartner was set, so defaultSelected below
  // picks it back up correctly without needing a key bump).
  const handleBackToSelection = () => {
    setSelectedPartner(undefined);
    setSelectedUtilityCode("");
    props.onClose();
  };

  // Auto-selected connector: stay in the dialog so the person can pick a different one.
  const handleChangeConnector = () => {
    userTouched.current = true;
    setAutoSelected(false);
    setLinkNote(null);
    setSelectedPartner(undefined);
    setSelectedUtilityCode("");
    setPendingSelection(undefined);
    setPartnerFilter(null);
    setTypeaheadResetKey((key) => key + 1);
  };

  const handleConfirmUtility = () => {
    if (!pendingSelection) {
      return;
    }
    setSelectedPartner(pendingSelection.partner);
    setSelectedUtilityCode(pendingSelection.utilityCode);
  };

  // Cancel any in-flight poll on unmount.
  useEffect(() => {
    return () => {
      pollCancelledRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const finishPoll = (
    phase: UapiPhase,
    status?: UapiStatus,
    message?: string
  ) => {
    pollCancelledRef.current = true;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    setUapiPhase(phase);
    if (message) setUapiError(message);
    if (status) props.onStatusChange?.(status);
  };

  const pollAuthStatus = async (
    formUid: string,
    deadline: number,
    failures: number
  ) => {
    if (pollCancelledRef.current) return;

    if (uapiWindowRef.current?.closed) {
      uapiWindowRef.current = null;
      setIsPopupClosed(true); // copy only - never implies success
    }

    let linked = false;
    let ok = false;
    try {
      const response = await api.Uapi.getAuthStatus({ formUid });
      if (pollCancelledRef.current) return;
      const result = response.data;
      if (result) {
        ok = true;
        // Backend casing/wording for `status` is unverifiable from this repo, and
        // `isAuthenticated` is the typed signal. Accept either.
        linked =
          result.isAuthenticated === true ||
          result.status?.toLowerCase() === UapiStatus.LINKED;
      } else {
        // getDataAsync swallows network/HTTP errors and returns data: undefined.
        log.warn("auth-status returned no data", response.message);
      }
    } catch (error) {
      // prepCallAsync (token refresh) throws out of getDataAsync.
      log.error("Error polling UAPI auth status", error);
    }
    if (pollCancelledRef.current) return;

    if (linked) {
      log.info("UAPI authorization confirmed", { formUid });
      finishPoll("linked", UapiStatus.LINKED);
      props.onClose();
      return;
    }

    const nextFailures = ok ? 0 : failures + 1;
    if (nextFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
      finishPoll(
        "error",
        UapiStatus.ERROR_CHECKING_AUTH_STATUS,
        "We couldn't check your authorization status. Please try again."
      );
      return;
    }

    if (Date.now() >= deadline) {
      log.info("UAPI auth status poll timed out", { formUid });
      finishPoll("timedOut"); // no status fired - dashboard/onboarding never advance on this
      return;
    }

    pollTimerRef.current = setTimeout(
      () => pollAuthStatus(formUid, deadline, nextFailures),
      POLL_INTERVAL_MS
    );
  };

  const handleOpenUapi = async () => {
    if (isOpeningUapi || uapiPhase === "awaiting") {
      return;
    }

    const zeosUserId = props.zeosUser?.zeosUserId ?? 0;
    const propertyId = props.property?.propertyId ?? 0;
    const emailAddress = props.zeosUser?.emailAddress ?? "";

    if (!zeosUserId || !propertyId || !emailAddress) {
      setUapiError(
        "Missing property or user information required to launch UAPI."
      );
      setUapiPhase("error");
      props.onStatusChange?.(UapiStatus.ERROR_STARTING_AUTH);
      return;
    }

    const request: IUapiFormRequest = {
      zeosUserId,
      propertyId,
      emailAddress,
    };

    try {
      setUapiError(null);
      setIsOpeningUapi(true);
      props.onStatusChange?.(UapiStatus.PENDING);

      const response = await api.Uapi.createForm(request);
      const embedUrl = response.data?.embedUrl;
      const formUid = response.data?.formUid;

      if (!embedUrl || !formUid) {
        setUapiError("Unable to launch the utility authorization form.");
        setUapiPhase("error");
        props.onStatusChange?.(UapiStatus.ERROR_STARTING_AUTH);
        return;
      }

      const newWindow = window.open(embedUrl, "uapi", "width=1000,height=800");

      if (!newWindow) {
        setUapiError(
          "We tried to open UAPI in a new window, but the browser blocked the popup. Please allow popups for this site and try again."
        );
        setUapiPhase("error");
        props.onStatusChange?.(UapiStatus.ERROR_STARTING_AUTH);
        return;
      }

      // Store window reference so the poll can flip the "popup closed" copy.
      uapiWindowRef.current = newWindow;
      setIsPopupClosed(false);
      setUapiPhase("awaiting");
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      pollCancelledRef.current = false;
      pollAuthStatus(formUid, Date.now() + POLL_TIMEOUT_MS, 0);
    } catch (error) {
      log.error("Failed to open UAPI form", error);
      setUapiError(
        "Something went wrong while launching UAPI. Please try again."
      );
      setUapiPhase("error");
      props.onStatusChange?.(UapiStatus.ERROR_STARTING_AUTH);
    } finally {
      setIsOpeningUapi(false);
    }
  };

  const openUploadPicker = (): void => uploadInputRef.current?.click();

  // Same de-dupe rule as UapiManual.tsx: skip exact re-adds (name+size+lastModified)
  // while still allowing same-named bills from different months.
  const onUploadFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length > 0) {
      setFiles((prev) => {
        const seen = new Set(prev.map(fileKey));
        const merged = [...prev];
        for (const f of picked) {
          const key = fileKey(f);
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(f);
          }
        }
        return merged;
      });
    }
    setUploadError(null);
    e.target.value = "";
  };

  const removeUploadFile = (index: number): void => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async (): Promise<void> => {
    const propertyId = props.property?.propertyId;
    if (!propertyId) {
      setUploadError("No property selected.");
      return;
    }
    if (files.length === 0) {
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      const uploads: DocumentUploadRequest[] = files.map((f) => ({
        entityTypeId: PROPERTY_ENTITY_TYPE_ID,
        entityId: propertyId,
        documentTypeId: getDocumentTypeId(f),
        documentCategoryId: UTILITY_BILL_CATEGORY_ID,
        file: f,
      }));

      const resp = await Document.createMultipleAsync(uploads);
      const status = String(
        (resp as any)?.zeosStatus ?? (resp as any)?.ZeosStatus ?? ""
      ).toLowerCase();
      if ((resp as any)?.error || status === "error") {
        throw new Error((resp as any)?.message || "Upload failed");
      }

      props.onStatusChange?.(UapiStatus.LINKED);
      props.onClose();
    } catch (error: any) {
      log.error("Bill snapshot upload failed", error);
      setUploadError(error?.message ?? "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (selectedPartner === "uapi") {
    if (uapiPhase === "awaiting") {
      return (
        <div className="flex flex-col gap-4">
          <ZeosSpinner
            message={
              isPopupClosed
                ? "Finishing up - confirming your authorization..."
                : "Waiting for you to finish authorizing in the popup window..."
            }
          />
          <p className="text-xs text-gray-600">
            Keep this window open. This can take a minute or two.
          </p>
        </div>
      );
    }

    if (uapiPhase === "linked") {
      return <p className="text-sm text-green-700">Utility account linked.</p>;
    }

    if (uapiPhase === "timedOut") {
      return (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-800">
            Your authorization was submitted and is still processing. It may take up to 5 minutes. In case of any problems contact our support team.
          </p>
          <div className="flex gap-2">
            <ZeosButton
              label="Close"
              type="button"
              mode="prev"
              onPress={props.onClose}
            />
          </div>
        </div>
      );
    }

    const canLaunchUapi =
      Boolean(props.property?.propertyId) &&
      Boolean(props.zeosUser?.zeosUserId) &&
      Boolean(props.zeosUser?.emailAddress);
    const actionLabel = isOpeningUapi
      ? "Opening Utility Authorization..."
      : uapiPhase === "error"
      ? "Try Again"
      : "Open Utility Authorization";

    return (
      <div className="flex flex-col gap-4">
        {uapiError && (
          <div className="text-sm text-red-600" role="alert">
            {uapiError}
          </div>
        )}
        <div>
          <ZeosButton
            label={actionLabel}
            type="button"
            mode="action"
            onPress={handleOpenUapi}
            isDisabled={!canLaunchUapi || isOpeningUapi}
          />
          <p className="mt-2 text-xs text-gray-600">
            We'll open the Utility API authorization page in a new window so you
            can finish linking your account.
          </p>
        </div>
        {autoSelected && linkNote && (
          <p className="text-sm text-gray-700">{linkNote}</p>
        )}
        <div className="flex gap-2">
          {autoSelected && (
            <ZeosButton
              label="Choose a different connector"
              type="button"
              mode="prev"
              onPress={handleChangeConnector}
            />
          )}
          <ZeosButton
            label="Cancel"
            type="button"
            mode="prev"
            onPress={handleBackToSelection}
          />
        </div>
      </div>
    );
  }

  if (selectedPartner === "bayou") {
    return (
      <>
      <BayouEmbed
        initialize={true}
        onClose={handleBackToSelection}
        onStatusChange={props.onStatusChange}
        propertyId={props.property?.propertyId ?? 0}
        showButtons={props.showBayouButtons ?? true}
        userEmail={props.zeosUser?.emailAddress ?? ""}
        utilityCode={selectedUtilityCode}
        zeosUserId={props.zeosUser?.zeosUserId ?? 0}
      />
      {autoSelected && (
        <div className="mt-3 flex flex-col gap-2">
          {linkNote && <p className="text-sm text-gray-700">{linkNote}</p>}
          <ZeosButton
            label="Choose a different connector"
            type="button"
            mode="prev"
            onPress={handleChangeConnector}
          />
        </div>
      )}
      </>
    );
  }

  if (mode === "upload") {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600">
          {manualUtilityName
            ? `${manualUtilityName} has no data connector. Upload a bill instead of starting an authorization.`
            : "Upload a utility bill to save a snapshot of its billing data. This does not connect a live utility account."}
        </p>
        <input
          ref={uploadInputRef}
          className="hidden"
          type="file"
          multiple
          accept={BILL_UPLOAD_ACCEPT}
          onChange={onUploadFileChange}
        />
        <div
          role="button"
          tabIndex={0}
          onClick={openUploadPicker}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") openUploadPicker();
          }}
          className="inline-block"
        >
          <ZeosButton
            label={files.length > 0 ? "Add More Files" : "Choose File(s)"}
            mode="action"
            type="button"
            hackWidth
          />
        </div>
        {files.length > 0 && (
          <ul className="space-y-1">
            {files.map((f, i) => (
              <li
                key={`${f.name}:${f.size}:${i}`}
                className="flex items-center gap-3 text-sm text-zeos-black"
              >
                <span className="break-all">
                  {f.name} ({Math.ceil(f.size / 1024)} KB)
                </span>
                <button
                  type="button"
                  onClick={() => removeUploadFile(i)}
                  className="text-red-600 underline shrink-0"
                  aria-label={`Remove ${f.name}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        {uploadError && (
          <div className="text-sm text-red-600" role="alert">
            {uploadError}
          </div>
        )}
        <div className="flex gap-2">
          <ZeosButton
            label={uploading ? "Uploading..." : "Upload"}
            type="button"
            mode="action"
            onPress={handleUpload}
            isDisabled={files.length === 0 || uploading}
          />
          <ZeosButton
            label="Back"
            type="button"
            mode="prev"
            onPress={() => {
              // The "no data connector" note belongs to the upload view; the picker lists every
              // UtilityAPI and Bayou utility, so repeating it there would contradict the list.
              setLinkNote(null);
              setMode("connect");
            }}
          />
        </div>
      </div>
    );
  }

  // Show typeahead for utility selection
  return (
    <>
      <div>
        {linkNote && <p className="mb-2 text-sm text-gray-700">{linkNote}</p>}
        <ZeosTypeAhead
          key={typeaheadResetKey}
          label="Select Utility"
          options={visibleOptions}
          placeholder="Search and select a utility..."
          onChange={handleUtilitySelection}
          clearable={true}
          defaultSelected={pendingSelection ? [pendingSelection] : undefined}
        />
        <div className="mt-4 flex gap-2">
          <ZeosButton
            label="Confirm"
            type="button"
            mode="action"
            onPress={handleConfirmUtility}
            isDisabled={!pendingSelection}
          />
          <ZeosButton
            label="Cancel"
            type="button"
            mode="prev"
            onPress={handleCancelSelection}
          />
        </div>
        {!props.uapiOnly && (
          <div className="mt-2 text-center text-sm text-gray-600">
            or{" "}
            <button
              type="button"
              className="underline text-zeos-black"
              onClick={() => {
                userTouched.current = true;
                setMode("upload");
              }}
            >
              upload a static bill snapshot
            </button>
          </div>
        )}
      </div>
    </>
  );
};
